import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Bell,
  Star,
  Check,
  X,
  AlertTriangle,
  Loader2,
  ChevronRight,
  Clock,
  ShieldCheck,
  MessageSquare,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const NotificationsPage = () => {
  const { token } = useAuth();
  const [pendingRatings, setPendingRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");

  const fetchPendingRatings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ratings/pending-confirmation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRatings(res.data);
    } catch (error) {
      console.error("Error fetching pending ratings:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPendingRatings();
    }
  }, [token, fetchPendingRatings]);

  const handleConfirm = async (ratingId) => {
    setProcessingId(ratingId);
    try {
      await axios.post(`${API}/ratings/${ratingId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Note confirmée ! Votre profil a été mis à jour.");
      setPendingRatings(prev => prev.filter(r => r.id !== ratingId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la confirmation");
    } finally {
      setProcessingId(null);
    }
  };

  const openDisputeDialog = (rating) => {
    setSelectedRating(rating);
    setDisputeReason("");
    setShowDisputeDialog(true);
  };

  const handleDispute = async () => {
    if (!selectedRating) return;
    
    setProcessingId(selectedRating.id);
    try {
      await axios.post(
        `${API}/ratings/${selectedRating.id}/dispute`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reason: disputeReason }
        }
      );
      toast.success("Note contestée. Un administrateur examinera votre demande.");
      setPendingRatings(prev => prev.filter(r => r.id !== selectedRating.id));
      setShowDisputeDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la contestation");
    } finally {
      setProcessingId(null);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  };

  const getCategoryLabel = (category) => {
    const categories = {
      comportement: { label: "Comportement", emoji: "😊" },
      ponctualite: { label: "Ponctualité", emoji: "⏰" },
      honnetete: { label: "Honnêteté", emoji: "🤝" },
      respect: { label: "Respect", emoji: "🙏" },
      aide: { label: "Aide apportée", emoji: "💪" },
      bienveillance: { label: "Bienveillance", emoji: "💖" }
    };
    return categories[category] || { label: category, emoji: "📝" };
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    return `il y a ${diffDays}j`;
  };

  return (
    <Layout>
      <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 pb-24">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Link to="/profile" className="p-2 bg-white rounded-full shadow hover:shadow-md transition-all">
              <ArrowLeft size={20} className="text-slate-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Outfit' }}>
                Notifications
              </h1>
              <p className="text-sm text-slate-500">Notes en attente de confirmation</p>
            </div>
          </div>

          {/* Pending Ratings */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#FF3366]" size={32} />
            </div>
          ) : pendingRatings.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Tout est à jour !</h3>
                <p className="text-slate-500 text-sm">
                  Vous n'avez aucune note en attente de confirmation.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">
                  {pendingRatings.length} note{pendingRatings.length > 1 ? 's' : ''} en attente
                </span>
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                  Action requise
                </span>
              </div>

              {pendingRatings.map((rating) => {
                const category = getCategoryLabel(rating.category);
                return (
                  <Card key={rating.id} className="border-0 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-3">
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-2">
                          <Bell size={16} />
                          <span className="text-sm font-medium">Nouvelle note reçue</span>
                        </div>
                        <span className="text-xs opacity-80">{formatTimeAgo(rating.created_at)}</span>
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      {/* Rater Info */}
                      <div className="flex items-center gap-3 mb-4">
                        <Avatar className="h-12 w-12 border-2 border-white shadow">
                          {rating.rater_photo ? (
                            <AvatarImage src={rating.rater_photo} alt={rating.rater_name} />
                          ) : null}
                          <AvatarFallback className="bg-gradient-to-br from-pink-100 to-pink-200 text-[#FF3366] font-bold">
                            {getInitials(rating.rater_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-bold text-slate-800">{rating.rater_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{rating.rater_level}</p>
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1 bg-amber-50 rounded-full">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < rating.stars ? "text-amber-400 fill-amber-400" : "text-slate-200"}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Category & Comment */}
                      <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{category.emoji}</span>
                          <span className="text-sm font-medium text-slate-700">{category.label}</span>
                        </div>
                        {rating.comment && (
                          <p className="text-sm text-slate-600 italic">"{rating.comment}"</p>
                        )}
                      </div>

                      {/* Info Notice */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-start gap-2">
                          <ShieldCheck size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-blue-700">
                            <strong>Confirmez</strong> si cette note correspond à la réalité, ou <strong>contestez</strong> si elle est injustifiée.
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => openDisputeDialog(rating)}
                          disabled={processingId === rating.id}
                          className="flex-1 h-12 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {processingId === rating.id ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <>
                              <X size={18} className="mr-2" />
                              Contester
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleConfirm(rating.id)}
                          disabled={processingId === rating.id}
                          className="flex-1 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                        >
                          {processingId === rating.id ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <>
                              <Check size={18} className="mr-2" />
                              Confirmer
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Info Card */}
          <Card className="border-0 shadow bg-gradient-to-br from-violet-50 to-purple-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-violet-800 mb-1">Comment ça marche ?</h4>
                  <ul className="text-sm text-violet-700 space-y-1">
                    <li>• Les notes confirmées sont ajoutées à votre profil</li>
                    <li>• Les notes contestées sont examinées par un admin</li>
                    <li>• Seules les notes confirmées comptent dans votre moyenne</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dispute Dialog */}
      <AlertDialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Contester cette note ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              <p className="mb-4">
                Expliquez pourquoi vous contestez cette note. Un administrateur examinera votre demande.
              </p>
              <Textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Raison de la contestation (optionnel)..."
                rows={3}
                className="w-full"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDispute}
              disabled={processingId}
              className="bg-red-600 hover:bg-red-700 rounded-xl"
            >
              {processingId ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : null}
              Contester
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default NotificationsPage;
